/**
 * Motion vectors (DBG-001), drawn only when the debug panel asks for them.
 *
 * Three arrows, because three quantities explain almost every "why did it do that?"
 * while tuning:
 *
 *   - **velocity** — where the ball is going, drawn as where it would be in
 *     `LOOKAHEAD_S` at its current speed, so the arrow has a physical length rather
 *     than an invented one.
 *   - **contact-patch slip** — `u = v + ω × (−R ẑ)`, the velocity of the bit of ball
 *     actually touching the cloth. This is what friction acts on, and it is the whole
 *     reason follow, draw and stun behave differently (ARCHITECTURE.md §5.4). A ball
 *     that is rolling truly has no slip arrow at all; a ball with draw has one
 *     pointing forwards while the ball travels forwards, which is exactly the picture.
 *   - **sidespin** — `ωz` as an arc around the ball, since it has no direction on the
 *     cloth and an arrow would be a lie.
 *
 * The arrays are the scene's scratch buffers, not objects, so drawing a frame at
 * 60 fps allocates nothing (ARCHITECTURE.md §5.2).
 */
import type { Viewport } from './viewport.ts';

export interface VectorPalette {
  readonly velocity: string;
  readonly slip: string;
  readonly spin: string;
}

export function readVectorPalette(): VectorPalette {
  const style = getComputedStyle(document.documentElement);
  const read = (name: string): string => style.getPropertyValue(name).trim();
  return {
    velocity: read('--color-vector-velocity'),
    slip: read('--color-vector-slip'),
    spin: read('--color-vector-spin'),
  };
}

/** How far ahead a velocity arrow reaches, in seconds of travel. */
const LOOKAHEAD_S = 0.15;
/** Below this, an arrow is shorter than its own head and just looks like noise. */
const MIN_ARROW_PX = 4;
/**
 * How far slip must differ from velocity before it is worth drawing, m/s.
 *
 * Immediately after a clean hit an object ball is in pure slide with no spin, so
 * `u` equals `v` exactly and the two arrows land on top of each other — which reads
 * as one arrow in the wrong colour. Drawing slip only when it has something to say
 * makes its presence the signal: if the blue arrow is there, spin is doing something.
 */
const SLIP_THRESHOLD_MPS = 0.05;

export interface VectorFields {
  readonly xM: Float64Array;
  readonly yM: Float64Array;
  readonly vxMps: Float64Array;
  readonly vyMps: Float64Array;
  readonly wxRadS: Float64Array;
  readonly wyRadS: Float64Array;
  readonly wzRadS: Float64Array;
  readonly potted: Uint8Array;
  readonly count: number;
}

export function drawVectors(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  palette: VectorPalette,
  radiusM: number,
  fields: VectorFields,
): void {
  const radius = viewport.lengthPx(radiusM);

  ctx.save();
  ctx.lineWidth = Math.max(1, radius * 0.14);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (let i = 0; i < fields.count; i++) {
    if (fields.potted[i] === 1) continue;

    const xM = fields.xM[i] ?? 0;
    const yM = fields.yM[i] ?? 0;
    const vx = fields.vxMps[i] ?? 0;
    const vy = fields.vyMps[i] ?? 0;
    const wx = fields.wxRadS[i] ?? 0;
    const wy = fields.wyRadS[i] ?? 0;
    const wz = fields.wzRadS[i] ?? 0;

    const cx = viewport.toScreenX(xM, yM);
    const cy = viewport.toScreenY(xM, yM);

    ctx.setLineDash([]);
    arrow(ctx, viewport, palette.velocity, cx, cy, xM, yM, vx, vy);

    // u = v + ω × (−R ẑ), written out: the cross product has only these two terms.
    const ux = vx + radiusM * wy;
    const uy = vy - radiusM * wx;
    const slipX = ux - vx;
    const slipY = uy - vy;
    if (Math.sqrt(slipX * slipX + slipY * slipY) > SLIP_THRESHOLD_MPS) {
      // Dashed, so it stays legible where it nearly coincides with the velocity.
      ctx.setLineDash([radius * 0.35, radius * 0.35]);
      arrow(ctx, viewport, palette.slip, cx, cy, xM, yM, ux, uy);
      ctx.setLineDash([]);
    }

    if (wz !== 0) {
      spinArc(ctx, palette.spin, cx, cy, radius, wz);
    }
  }

  ctx.restore();
}

function arrow(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  colour: string,
  cx: number,
  cy: number,
  xM: number,
  yM: number,
  vxMps: number,
  vyMps: number,
): void {
  const tipXM = xM + vxMps * LOOKAHEAD_S;
  const tipYM = yM + vyMps * LOOKAHEAD_S;
  const tipX = viewport.toScreenX(tipXM, tipYM);
  const tipY = viewport.toScreenY(tipXM, tipYM);

  const dx = tipX - cx;
  const dy = tipY - cy;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < MIN_ARROW_PX) return;

  const ux = dx / length;
  const uy = dy / length;
  const head = Math.min(length * 0.4, ctx.lineWidth * 3);

  ctx.strokeStyle = colour;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.lineTo(tipX, tipY);
  // Head, as two strokes back from the tip at roughly 30° either side.
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(tipX - ux * head + uy * head * 0.5, tipY - uy * head - ux * head * 0.5);
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(tipX - ux * head - uy * head * 0.5, tipY - uy * head + ux * head * 0.5);
  ctx.stroke();
}

/** Sidespin, as an arc around the ball. Its length reads as magnitude, its gap as sense. */
function spinArc(
  ctx: CanvasRenderingContext2D,
  colour: string,
  cx: number,
  cy: number,
  radius: number,
  wzRadS: number,
): void {
  // Saturates at 4π rad/s so a big spin does not simply draw a full circle.
  const sweep = Math.min(Math.abs(wzRadS) / (4 * Math.PI), 1) * Math.PI * 1.5;
  if (sweep < 0.2) return;

  ctx.strokeStyle = colour;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 1.45, 0, sweep, wzRadS < 0);
  ctx.stroke();
}
